import React, { useState, Children } from 'react';
import { PlusIcon, TrashIcon, ArrowLeftIcon, UserPlusIcon, MailIcon, BuildingIcon, GlobeIcon, SaveIcon, CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon, BarChartIcon, CreditCardIcon } from 'lucide-react';
import { FileUpload } from '../FileUpload';
export function VendorOnboarding({
  onSave,
  onSendInvite,
  setCurrentView,
  orgUnits = [],
  locale = 'en-US'
}) {
  const [onboardingMethod, setOnboardingMethod] = useState('direct');
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    description: '',
    country: '',
    taxId: '',
    website: '',
    contactName: '',
    email: '',
    phone: '',
    role: '',
    // Address fields
    street: '',
    city: '',
    state: '',
    postalCode: '',
    addressCountry: '',
    addressType: 'Physical',
    isPrimary: true,
    // Entity and site restrictions
    isGlobal: true,
    orgUnitId: null,
    includeChildOrgUnits: false,
    // ESG data
    esg: {
      score: '',
      environmental: '',
      social: '',
      governance: '',
      summary: ''
    },
    // Bank account fields
    bankName: '',
    accountName: '',
    accountNumber: '',
    routingNumber: '',
    swiftCode: '',
    iban: '',
    accountType: 'Checking'
  });
  const [inviteData, setInviteData] = useState({
    name: '',
    type: '',
    email: '',
    contactName: '',
    phone: '',
    role: '',
    message: 'We invite you to register as a vendor in our system. Please complete the onboarding process by following the link that will be sent to your email.'
  });
  const [errors, setErrors] = useState({});
  const [contacts, setContacts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [files, setFiles] = useState([]);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showBankAccountForm, setShowBankAccountForm] = useState(false);
  const [showESGForm, setShowESGForm] = useState(false);
  const [isESGEnabled] = useState(true); // In a real app, this would come from tenant settings
  const [hasESGEditPermission] = useState(true); // In a real app, this would come from user permissions
  const countries = ['United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Japan', 'China', 'India'];
  const vendorTypes = ['Supplier', 'Manufacturer', 'Distributor', 'Service Provider', 'Consultant', 'Contractor', 'Other'];
  const addressTypes = ['Physical', 'Billing', 'Shipping', 'Mailing', 'Registered', 'Other'];
  const accountTypes = ['Checking', 'Savings', 'Current', 'Business', 'Corporate'];
  // Handle availability type change
  const handleAvailabilityChange = isGlobal => {
    setFormData({
      ...formData,
      isGlobal,
      // Clear org unit selections when switching to global
      orgUnitId: isGlobal ? null : formData.orgUnitId,
      includeChildOrgUnits: isGlobal ? false : formData.includeChildOrgUnits
    });
  };
  const handleInputChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    if (name.startsWith('esg.')) {
      const esgField = name.split('.')[1];
      setFormData({
        ...formData,
        esg: {
          ...formData.esg,
          [esgField]: value
        }
      });
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const handleInviteInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setInviteData({
      ...inviteData,
      [name]: value
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const addContact = () => {
    if (!formData.contactName || !formData.email) {
      setErrors({
        ...errors,
        contactName: !formData.contactName ? 'Contact name is required' : null,
        email: !formData.email ? 'Email is required' : null
      });
      return;
    }
    const newContact = {
      id: contacts.length + 1,
      name: formData.contactName,
      email: formData.email,
      phone: formData.phone || '',
      role: formData.role || ''
    };
    setContacts([...contacts, newContact]);
    setFormData({
      ...formData,
      contactName: '',
      email: '',
      phone: '',
      role: ''
    });
  };
  const removeContact = id => {
    setContacts(contacts.filter(contact => contact.id !== id));
  };
  const handleAddAddress = () => {
    if (!formData.street || !formData.city || !formData.addressCountry) {
      setErrors({
        ...errors,
        street: !formData.street ? 'Street is required' : null,
        city: !formData.city ? 'City is required' : null,
        addressCountry: !formData.addressCountry ? 'Country is required' : null
      });
      return;
    }
    const newAddress = {
      id: editingAddress ? editingAddress.id : addresses.length + 1,
      street: formData.street,
      city: formData.city,
      state: formData.state,
      postalCode: formData.postalCode,
      country: formData.addressCountry,
      addressType: formData.addressType,
      isPrimary: formData.isPrimary
    };
    let updatedAddresses = [];
    if (editingAddress) {
      // Update existing address
      updatedAddresses = addresses.map(addr => addr.id === editingAddress.id ? newAddress : addr);
    } else {
      // Add new address
      // If this is marked as primary, update all others to non-primary
      if (newAddress.isPrimary) {
        updatedAddresses = [...addresses.map(addr => ({
          ...addr,
          isPrimary: false
        })), newAddress];
      } else if (addresses.length === 0) {
        // First address should be primary by default
        updatedAddresses = [{
          ...newAddress,
          isPrimary: true
        }];
      } else {
        updatedAddresses = [...addresses, newAddress];
      }
    }
    setAddresses(updatedAddresses);
    setFormData({
      ...formData,
      street: '',
      city: '',
      state: '',
      postalCode: '',
      addressCountry: '',
      addressType: 'Physical',
      isPrimary: true
    });
    setShowAddressForm(false);
    setEditingAddress(null);
  };
  const handleEditAddress = address => {
    setEditingAddress(address);
    setFormData({
      ...formData,
      street: address.street,
      city: address.city,
      state: address.state || '',
      postalCode: address.postalCode || '',
      addressCountry: address.country,
      addressType: address.addressType || 'Physical',
      isPrimary: address.isPrimary || false
    });
    setShowAddressForm(true);
  };
  const updateAddress = () => {
    if (!formData.street || !formData.city || !formData.addressCountry) {
      setErrors({
        ...errors,
        street: !formData.street ? 'Street is required' : null,
        city: !formData.city ? 'City is required' : null,
        addressCountry: !formData.addressCountry ? 'Country is required' : null
      });
      return;
    }
    const updatedAddress = {
      id: editingAddress.id,
      street: formData.street,
      city: formData.city,
      state: formData.state || '',
      postalCode: formData.postalCode || '',
      country: formData.addressCountry,
      addressType: formData.addressType || 'Physical',
      isPrimary: formData.isPrimary
    };
    // Update existing address
    let updatedAddresses = addresses.map(address => address.id === editingAddress.id ? updatedAddress : address);
    // If this address is now primary, update all others to not be primary
    if (updatedAddress.isPrimary) {
      updatedAddresses = updatedAddresses.map(addr => addr.id === editingAddress.id ? addr : {
        ...addr,
        isPrimary: false
      });
    }
    setAddresses(updatedAddresses);
    // Clear form and exit edit mode
    setFormData({
      ...formData,
      street: '',
      city: '',
      state: '',
      postalCode: '',
      addressCountry: '',
      addressType: 'Physical',
      isPrimary: false
    });
    setEditingAddress(null);
    setShowAddressForm(false);
  };
  const removeAddress = id => {
    const addressToRemove = addresses.find(addr => addr.id === id);
    const wasRemovingPrimary = addressToRemove && addressToRemove.isPrimary;
    const filteredAddresses = addresses.filter(address => address.id !== id);
    // If we removed the primary address and there are other addresses left,
    // make the first one primary
    if (wasRemovingPrimary && filteredAddresses.length > 0) {
      filteredAddresses[0].isPrimary = true;
    }
    setAddresses(filteredAddresses);
  };
  // Bank Account functions
  const handleAddBankAccount = () => {
    if (!formData.bankName || !formData.accountName || !formData.accountNumber) {
      setErrors({
        ...errors,
        bankName: !formData.bankName ? 'Bank name is required' : null,
        accountName: !formData.accountName ? 'Account name is required' : null,
        accountNumber: !formData.accountNumber ? 'Account number is required' : null
      });
      return;
    }
    const newBankAccount = {
      id: editingBankAccount ? editingBankAccount.id : bankAccounts.length + 1,
      bankName: formData.bankName,
      accountName: formData.accountName,
      accountNumber: formData.accountNumber,
      routingNumber: formData.routingNumber,
      swiftCode: formData.swiftCode,
      iban: formData.iban,
      accountType: formData.accountType,
      isPrimary: formData.isPrimary,
      country: formData.addressCountry || formData.country
    };
    let updatedBankAccounts = [];
    if (editingBankAccount) {
      // Update existing bank account
      updatedBankAccounts = bankAccounts.map(account => account.id === editingBankAccount.id ? newBankAccount : account);
    } else {
      // Add new bank account
      // If this is marked as primary, update all others to non-primary
      if (newBankAccount.isPrimary) {
        updatedBankAccounts = [...bankAccounts.map(account => ({
          ...account,
          isPrimary: false
        })), newBankAccount];
      } else if (bankAccounts.length === 0) {
        // First bank account should be primary by default
        updatedBankAccounts = [{
          ...newBankAccount,
          isPrimary: true
        }];
      } else {
        updatedBankAccounts = [...bankAccounts, newBankAccount];
      }
    }
    setBankAccounts(updatedBankAccounts);
    setFormData({
      ...formData,
      bankName: '',
      accountName: '',
      accountNumber: '',
      routingNumber: '',
      swiftCode: '',
      iban: '',
      accountType: 'Checking',
      isPrimary: false
    });
    setShowBankAccountForm(false);
    setEditingBankAccount(null);
  };
  const handleEditBankAccount = account => {
    setEditingBankAccount(account);
    setFormData({
      ...formData,
      bankName: account.bankName,
      accountName: account.accountName,
      accountNumber: account.accountNumber,
      routingNumber: account.routingNumber || '',
      swiftCode: account.swiftCode || '',
      iban: account.iban || '',
      accountType: account.accountType || 'Checking',
      isPrimary: account.isPrimary || false
    });
    setShowBankAccountForm(true);
  };
  const removeBankAccount = id => {
    const accountToRemove = bankAccounts.find(account => account.id === id);
    const wasRemovingPrimary = accountToRemove && accountToRemove.isPrimary;
    const filteredAccounts = bankAccounts.filter(account => account.id !== id);
    // If we removed the primary account and there are other accounts left,
    // make the first one primary
    if (wasRemovingPrimary && filteredAccounts.length > 0) {
      filteredAccounts[0].isPrimary = true;
    }
    setBankAccounts(filteredAccounts);
  };
  const cancelBankAccountEdit = () => {
    setFormData({
      ...formData,
      bankName: '',
      accountName: '',
      accountNumber: '',
      routingNumber: '',
      swiftCode: '',
      iban: '',
      accountType: 'Checking',
      isPrimary: false
    });
    setEditingBankAccount(null);
    setShowBankAccountForm(false);
  };
  const handleFileUpload = newFiles => {
    setFiles([...files, ...newFiles]);
  };
  const removeFile = index => {
    setFiles(files.filter((_, i) => i !== index));
  };
  const validateForm = () => {
    const newErrors = {};
    if (onboardingMethod === 'direct') {
      // Validate required fields for direct onboarding
      if (!formData.name) newErrors.name = 'Vendor name is required';
      if (!formData.type) newErrors.type = 'Vendor type is required';
      if (!formData.country) newErrors.country = 'Country is required';
      if (contacts.length === 0) newErrors.contacts = 'At least one contact is required';
      if (addresses.length === 0) newErrors.addresses = 'At least one address is required';
      // Validate entity selections if not global
      if (!formData.isGlobal && !formData.orgUnitId) {
        newErrors.orgUnitId = 'An organisation unit must be selected';
      }
      // Validate ESG data if provided
      if (formData.esg && formData.esg.score) {
        const score = parseInt(formData.esg.score);
        if (isNaN(score) || score < 0 || score > 100) {
          newErrors['esg.score'] = 'Score must be a number between 0 and 100';
        }
        if (formData.esg.environmental) {
          const envScore = parseInt(formData.esg.environmental);
          if (isNaN(envScore) || envScore < 0 || envScore > 100) {
            newErrors['esg.environmental'] = 'Score must be a number between 0 and 100';
          }
        }
        if (formData.esg.social) {
          const socialScore = parseInt(formData.esg.social);
          if (isNaN(socialScore) || socialScore < 0 || socialScore > 100) {
            newErrors['esg.social'] = 'Score must be a number between 0 and 100';
          }
        }
        if (formData.esg.governance) {
          const govScore = parseInt(formData.esg.governance);
          if (isNaN(govScore) || govScore < 0 || govScore > 100) {
            newErrors['esg.governance'] = 'Score must be a number between 0 and 100';
          }
        }
      }
    } else {
      // Validate required fields for invite
      if (!inviteData.name) newErrors.name = 'Vendor name is required';
      if (!inviteData.email) newErrors.email = 'Email is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    if (onboardingMethod === 'direct') {
      // Prepare data for saving
      const vendorData = {
        ...formData,
        status: 'Active',
        complianceStatus: files.length > 0 ? 'Pending Review' : 'Incomplete',
        contacts,
        addresses,
        bankAccounts,
        documents: files.map((file, index) => ({
          id: index + 1,
          name: file.name,
          type: 'Supporting Document',
          uploadDate: new Date().toISOString().split('T')[0],
          expiryDate: null,
          status: 'Valid'
        })),
        esg: formData.esg.score ? {
          score: parseInt(formData.esg.score),
          environmental: formData.esg.environmental ? parseInt(formData.esg.environmental) : null,
          social: formData.esg.social ? parseInt(formData.esg.social) : null,
          governance: formData.esg.governance ? parseInt(formData.esg.governance) : null,
          summary: formData.esg.summary || '',
          lastUpdated: new Date().toISOString().split('T')[0]
        } : null
      };
      // Call the save handler from parent
      onSave(vendorData);
    } else {
      // Send invitation with availability settings
      const inviteWithAvailability = {
        ...inviteData,
        isGlobal: formData.isGlobal,
        orgUnitId: formData.orgUnitId,
        includeChildOrgUnits: formData.includeChildOrgUnits
      };
      onSendInvite(inviteWithAvailability);
    }
  };
  const cancelAddressEdit = () => {
    setFormData({
      ...formData,
      street: '',
      city: '',
      state: '',
      postalCode: '',
      addressCountry: '',
      addressType: 'Physical',
      isPrimary: false
    });
    setEditingAddress(null);
    setShowAddressForm(false);
  };
  return <div data-id="element-22705">
      <div className="mb-8 flex items-center" data-id="element-22706">
        <button onClick={() => setCurrentView('vendors')} className="mr-4 text-gray-400 hover:text-gray-500" data-id="element-22707">
          <ArrowLeftIcon className="h-5 w-5" data-id="element-22708" />
        </button>
        <div data-id="element-22709">
          <h3 className="text-xl font-semibold text-gray-900" data-id="element-22710">
            Add New Vendor
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-22711">
            Create a new vendor record or invite a vendor to onboard themselves
          </p>
        </div>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-22712">
        <div className="px-4 py-5 sm:px-6" data-id="element-22713">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22714">
            Onboarding Method
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-22715">
            Choose how you want to onboard this vendor
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-22716">
          <div className="flex flex-col sm:flex-row sm:space-x-4" data-id="element-22717">
            <div className={`flex-1 p-4 border rounded-lg mb-4 sm:mb-0 cursor-pointer ${onboardingMethod === 'direct' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`} onClick={() => setOnboardingMethod('direct')} data-id="element-22718">
              <div className="flex items-center" data-id="element-22719">
                <div className="flex-shrink-0" data-id="element-22720">
                  <div className={`h-5 w-5 rounded-full border ${onboardingMethod === 'direct' ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'} flex items-center justify-center`} data-id="element-22721">
                    {onboardingMethod === 'direct' && <CheckIcon className="h-3 w-3 text-white" data-id="element-22722" />}
                  </div>
                </div>
                <div className="ml-3" data-id="element-22723">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-22724">
                    Direct Entry
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-22725">
                    Enter vendor information directly into the system
                  </p>
                </div>
              </div>
            </div>
            <div className={`flex-1 p-4 border rounded-lg cursor-pointer ${onboardingMethod === 'invite' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`} onClick={() => setOnboardingMethod('invite')} data-id="element-22726">
              <div className="flex items-center" data-id="element-22727">
                <div className="flex-shrink-0" data-id="element-22728">
                  <div className={`h-5 w-5 rounded-full border ${onboardingMethod === 'invite' ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'} flex items-center justify-center`} data-id="element-22729">
                    {onboardingMethod === 'invite' && <CheckIcon className="h-3 w-3 text-white" data-id="element-22730" />}
                  </div>
                </div>
                <div className="ml-3" data-id="element-22731">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-22732">
                    Send Invitation
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-22733">
                    Send an invitation to the vendor to complete their own
                    profile
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {onboardingMethod === 'direct' ? <>
          {/* Basic Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-22734">
            <div className="px-4 py-5 sm:px-6" data-id="element-22735">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22736">
                Basic Information
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-22737">
                Enter vendor details and basic information
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-22738">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-22739">
                <div className="sm:col-span-3" data-id="element-22740">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-22741">
                    Vendor Name <span className="text-red-500" data-id="element-22742">*</span>
                  </label>
                  <div className="mt-1" data-id="element-22743">
                    <input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.name ? 'border-red-300' : ''}`} data-id="element-22744" />
                    {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-22745">{errors.name}</p>}
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-22746">
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700" data-id="element-22747">
                    Vendor Type <span className="text-red-500" data-id="element-22748">*</span>
                  </label>
                  <div className="mt-1" data-id="element-22749">
                    <select id="type" name="type" value={formData.type} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.type ? 'border-red-300' : ''}`} data-id="element-22750">
                      <option value="" data-id="element-22751">Select vendor type</option>
                      {vendorTypes.map(type => <option key={type} value={type} data-id="element-22752">
                          {type}
                        </option>)}
                    </select>
                    {errors.type && <p className="mt-1 text-sm text-red-600" data-id="element-22753">{errors.type}</p>}
                  </div>
                </div>
                <div className="sm:col-span-6" data-id="element-22754">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-22755">
                    Description
                  </label>
                  <div className="mt-1" data-id="element-22756">
                    <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-22757" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-22758">
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700" data-id="element-22759">
                    Country <span className="text-red-500" data-id="element-22760">*</span>
                  </label>
                  <div className="mt-1" data-id="element-22761">
                    <select id="country" name="country" value={formData.country} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.country ? 'border-red-300' : ''}`} data-id="element-22762">
                      <option value="" data-id="element-22763">Select country</option>
                      {countries.map(country => <option key={country} value={country} data-id="element-22764">
                          {country}
                        </option>)}
                    </select>
                    {errors.country && <p className="mt-1 text-sm text-red-600" data-id="element-22765">
                        {errors.country}
                      </p>}
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-22766">
                  <label htmlFor="taxId" className="block text-sm font-medium text-gray-700" data-id="element-22767">
                    Tax ID / Business Number
                  </label>
                  <div className="mt-1" data-id="element-22768">
                    <input type="text" name="taxId" id="taxId" value={formData.taxId} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-22769" />
                  </div>
                </div>
                <div className="sm:col-span-6" data-id="element-22770">
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700" data-id="element-22771">
                    Website
                  </label>
                  <div className="mt-1" data-id="element-22772">
                    <input type="url" name="website" id="website" value={formData.website} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-22773" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* ESG Information */}
          {isESGEnabled && hasESGEditPermission && <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-22774">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-22775">
                <div data-id="element-22776">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22777">
                    ESG Information
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-22778">
                    Environmental, Social, and Governance data
                  </p>
                </div>
                <button type="button" onClick={() => setShowESGForm(!showESGForm)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22779">
                  {showESGForm ? <>
                      <ChevronUpIcon className="h-4 w-4 mr-1" data-id="element-22780" />
                      Hide ESG Form
                    </> : <>
                      <ChevronDownIcon className="h-4 w-4 mr-1" data-id="element-22781" />
                      Add ESG Data
                    </>}
                </button>
              </div>
              {showESGForm && <div className="border-t border-gray-200 px-4 py-5" data-id="element-22782">
                  <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-22783">
                    <div className="sm:col-span-2" data-id="element-22784">
                      <label htmlFor="esg.score" className="block text-sm font-medium text-gray-700" data-id="element-22785">
                        Overall ESG Score (0-100)
                      </label>
                      <div className="mt-1" data-id="element-22786">
                        <input type="number" name="esg.score" id="esg.score" min="0" max="100" value={formData.esg.score} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors['esg.score'] ? 'border-red-300' : ''}`} data-id="element-22787" />
                        {errors['esg.score'] && <p className="mt-1 text-sm text-red-600" data-id="element-22788">
                            {errors['esg.score']}
                          </p>}
                      </div>
                    </div>
                    <div className="sm:col-span-2" data-id="element-22789">
                      <label htmlFor="esg.environmental" className="block text-sm font-medium text-gray-700" data-id="element-22790">
                        Environmental Score (0-100)
                      </label>
                      <div className="mt-1" data-id="element-22791">
                        <input type="number" name="esg.environmental" id="esg.environmental" min="0" max="100" value={formData.esg.environmental} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors['esg.environmental'] ? 'border-red-300' : ''}`} data-id="element-22792" />
                        {errors['esg.environmental'] && <p className="mt-1 text-sm text-red-600" data-id="element-22793">
                            {errors['esg.environmental']}
                          </p>}
                      </div>
                    </div>
                    <div className="sm:col-span-2" data-id="element-22794">
                      <label htmlFor="esg.social" className="block text-sm font-medium text-gray-700" data-id="element-22795">
                        Social Score (0-100)
                      </label>
                      <div className="mt-1" data-id="element-22796">
                        <input type="number" name="esg.social" id="esg.social" min="0" max="100" value={formData.esg.social} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors['esg.social'] ? 'border-red-300' : ''}`} data-id="element-22797" />
                        {errors['esg.social'] && <p className="mt-1 text-sm text-red-600" data-id="element-22798">
                            {errors['esg.social']}
                          </p>}
                      </div>
                    </div>
                    <div className="sm:col-span-3" data-id="element-22799">
                      <label htmlFor="esg.governance" className="block text-sm font-medium text-gray-700" data-id="element-22800">
                        Governance Score (0-100)
                      </label>
                      <div className="mt-1" data-id="element-22801">
                        <input type="number" name="esg.governance" id="esg.governance" min="0" max="100" value={formData.esg.governance} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors['esg.governance'] ? 'border-red-300' : ''}`} data-id="element-22802" />
                        {errors['esg.governance'] && <p className="mt-1 text-sm text-red-600" data-id="element-22803">
                            {errors['esg.governance']}
                          </p>}
                      </div>
                    </div>
                    <div className="sm:col-span-6" data-id="element-22804">
                      <label htmlFor="esg.summary" className="block text-sm font-medium text-gray-700" data-id="element-22805">
                        ESG Summary
                      </label>
                      <div className="mt-1" data-id="element-22806">
                        <textarea id="esg.summary" name="esg.summary" rows={3} value={formData.esg.summary} onChange={handleInputChange} placeholder="Brief summary of vendor's ESG initiatives and performance" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-22807" />
                      </div>
                    </div>
                  </div>
                </div>}
            </div>}
          {/* Contact Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-22808">
            <div className="px-4 py-5 sm:px-6" data-id="element-22809">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22810">
                Contact Information
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-22811">
                Add primary and additional contacts for this vendor
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-22812">
              <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden" data-id="element-22813">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-22814">
                  <h4 className="text-base font-medium text-gray-900" data-id="element-22815">
                    Add Contact
                  </h4>
                </div>
                <div className="p-4" data-id="element-22816">
                  <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2" data-id="element-22817">
                    <div data-id="element-22818">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22819">
                        Contact Name <span className="text-red-500" data-id="element-22820">*</span>
                      </label>
                      <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.contactName ? 'border-red-300' : ''}`} value={formData.contactName} name="contactName" onChange={handleInputChange} placeholder="Full name" data-id="element-22821" />
                      {errors.contactName && <p className="mt-1 text-sm text-red-600" data-id="element-22822">
                          {errors.contactName}
                        </p>}
                    </div>
                    <div data-id="element-22823">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22824">
                        Email <span className="text-red-500" data-id="element-22825">*</span>
                      </label>
                      <input type="email" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.email ? 'border-red-300' : ''}`} value={formData.email} name="email" onChange={handleInputChange} placeholder="Email address" data-id="element-22826" />
                      {errors.email && <p className="mt-1 text-sm text-red-600" data-id="element-22827">
                          {errors.email}
                        </p>}
                    </div>
                    <div data-id="element-22828">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22829">
                        Phone
                      </label>
                      <input type="tel" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.phone} name="phone" onChange={handleInputChange} placeholder="Phone number" data-id="element-22830" />
                    </div>
                    <div data-id="element-22831">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22832">
                        Role
                      </label>
                      <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.role} name="role" onChange={handleInputChange} placeholder="Job title or role" data-id="element-22833" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-22834">
                    <button type="button" onClick={addContact} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22835">
                      <PlusIcon className="h-4 w-4 mr-1" data-id="element-22836" />
                      Add Contact
                    </button>
                  </div>
                </div>
              </div>
              {errors.contacts && <div className="mb-4" data-id="element-22837">
                  <p className="text-sm text-red-600" data-id="element-22838">{errors.contacts}</p>
                </div>}
              {contacts.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-22839">
                  {contacts.map(contact => <li key={contact.id} className="py-4 flex items-center" data-id="element-22840">
                      <div className="flex-shrink-0" data-id="element-22841">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500" data-id="element-22842">
                          <UserPlusIcon className="h-5 w-5" data-id="element-22843" />
                        </div>
                      </div>
                      <div className="ml-3 flex-1" data-id="element-22844">
                        <p className="text-sm font-medium text-gray-900" data-id="element-22845">
                          {contact.name}
                        </p>
                        <div className="flex items-center text-sm text-gray-500" data-id="element-22846">
                          <MailIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-22847" />
                          {contact.email}
                        </div>
                        {contact.phone && <p className="mt-1 text-sm text-gray-500" data-id="element-22848">
                            {contact.phone}
                          </p>}
                        {contact.role && <p className="mt-1 text-sm text-gray-500" data-id="element-22849">
                            {contact.role}
                          </p>}
                      </div>
                      <div className="ml-4" data-id="element-22850">
                        <button type="button" onClick={() => removeContact(contact.id)} className="text-red-600 hover:text-red-900" data-id="element-22851">
                          <TrashIcon className="h-5 w-5" data-id="element-22852" />
                        </button>
                      </div>
                    </li>)}
                </ul> : <div className="text-center py-4 text-sm text-gray-500" data-id="element-22853">
                  No contacts added yet. Add at least one contact.
                </div>}
            </div>
          </div>
          {/* Address Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-22854">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-22855">
              <div data-id="element-22856">
                <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22857">
                  Address Information
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-22858">
                  Add addresses for this vendor
                </p>
              </div>
              <button type="button" onClick={() => setShowAddressForm(!showAddressForm)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22859">
                {showAddressForm ? <>
                    <ChevronUpIcon className="h-4 w-4 mr-1" data-id="element-22860" />
                    Hide Address Form
                  </> : <>
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-22861" />
                    Add Address
                  </>}
              </button>
            </div>
            {showAddressForm && <div className="px-4 py-5 border-t border-gray-200" data-id="element-22862">
                <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden" data-id="element-22863">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-22864">
                    <h4 className="text-base font-medium text-gray-900" data-id="element-22865">
                      {editingAddress ? 'Edit Address' : 'Add New Address'}
                    </h4>
                  </div>
                  <div className="p-4" data-id="element-22866">
                    <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2" data-id="element-22867">
                      <div data-id="element-22868">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22869">
                          Street Address <span className="text-red-500" data-id="element-22870">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.street ? 'border-red-300' : ''}`} value={formData.street} name="street" onChange={handleInputChange} placeholder="Street address" data-id="element-22871" />
                        {errors.street && <p className="mt-1 text-sm text-red-600" data-id="element-22872">
                            {errors.street}
                          </p>}
                      </div>
                      <div data-id="element-22873">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22874">
                          City <span className="text-red-500" data-id="element-22875">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.city ? 'border-red-300' : ''}`} value={formData.city} name="city" onChange={handleInputChange} placeholder="City" data-id="element-22876" />
                        {errors.city && <p className="mt-1 text-sm text-red-600" data-id="element-22877">
                            {errors.city}
                          </p>}
                      </div>
                      <div data-id="element-22878">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22879">
                          State/Province
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.state} name="state" onChange={handleInputChange} placeholder="State or province" data-id="element-22880" />
                      </div>
                      <div data-id="element-22881">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22882">
                          Postal Code
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.postalCode} name="postalCode" onChange={handleInputChange} placeholder="Postal code" data-id="element-22883" />
                      </div>
                      <div data-id="element-22884">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22885">
                          Country <span className="text-red-500" data-id="element-22886">*</span>
                        </label>
                        <select className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.addressCountry ? 'border-red-300' : ''}`} value={formData.addressCountry} name="addressCountry" onChange={handleInputChange} data-id="element-22887">
                          <option value="" data-id="element-22888">Select country</option>
                          {countries.map(country => <option key={country} value={country} data-id="element-22889">
                              {country}
                            </option>)}
                        </select>
                        {errors.addressCountry && <p className="mt-1 text-sm text-red-600" data-id="element-22890">
                            {errors.addressCountry}
                          </p>}
                      </div>
                      <div data-id="element-22891">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22892">
                          Address Type
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.addressType} name="addressType" onChange={handleInputChange} data-id="element-22893">
                          {addressTypes.map(type => <option key={type} value={type} data-id="element-22894">
                              {type}
                            </option>)}
                        </select>
                      </div>
                      <div className="flex items-center mt-4" data-id="element-22895">
                        <input id="isPrimary" name="isPrimary" type="checkbox" checked={formData.isPrimary} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-22896" />
                        <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-700" data-id="element-22897">
                          Primary Address
                        </label>
                      </div>
                    </div>
                    <div className="mt-4 flex space-x-3" data-id="element-22898">
                      <button type="button" onClick={handleAddAddress} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22899">
                        {editingAddress ? 'Update Address' : 'Add Address'}
                      </button>
                      <button type="button" onClick={() => {
                  setShowAddressForm(false);
                  setEditingAddress(null);
                }} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22900">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>}
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-22901">
              {errors.addresses && <div className="mb-4" data-id="element-22902">
                  <p className="text-sm text-red-600" data-id="element-22903">{errors.addresses}</p>
                </div>}
              {addresses.length > 0 ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-22904">
                  {addresses.map(address => <div key={address.id} className={`relative rounded-lg border ${address.isPrimary ? 'border-indigo-500' : 'border-gray-300'} bg-white px-4 py-3 shadow-sm flex flex-col space-y-2`} data-id="element-22905">
                      {address.isPrimary && <span className="absolute top-0 right-0 -mt-2 -mr-2 px-2 py-1 text-xs font-bold rounded-full bg-indigo-100 text-indigo-800 flex items-center" data-id="element-22906">
                          <CheckIcon className="h-3 w-3 mr-1" data-id="element-22907" />
                          Primary
                        </span>}
                      <div className="flex items-center space-x-3" data-id="element-22908">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-22909">
                          <BuildingIcon className="h-5 w-5 text-gray-500" data-id="element-22910" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-22911">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-22912">
                            {address.addressType}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-22913">
                        <p data-id="element-22914">{address.street}</p>
                        <p data-id="element-22915">
                          {address.city}
                          {address.state && `, ${address.state}`}{' '}
                          {address.postalCode}
                        </p>
                        <p data-id="element-22916">{address.country}</p>
                      </div>
                      <div className="flex justify-end space-x-2 mt-2" data-id="element-22917">
                        <button type="button" onClick={() => handleEditAddress(address)} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-22918">
                          Edit
                        </button>
                        <button type="button" onClick={() => removeAddress(address.id)} className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-22919">
                          Remove
                        </button>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-4 text-sm text-gray-500" data-id="element-22920">
                  No addresses added yet. Add at least one address.
                </div>}
            </div>
          </div>
          {/* Bank Account Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-22921">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-22922">
              <div data-id="element-22923">
                <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22924">
                  Bank Account Information
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-22925">
                  Add payment bank accounts for this vendor
                </p>
              </div>
              <button type="button" onClick={() => setShowBankAccountForm(!showBankAccountForm)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22926">
                {showBankAccountForm ? <>
                    <ChevronUpIcon className="h-4 w-4 mr-1" data-id="element-22927" />
                    Hide Bank Account Form
                  </> : <>
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-22928" />
                    Add Bank Account
                  </>}
              </button>
            </div>
            {showBankAccountForm && <div className="px-4 py-5 border-t border-gray-200" data-id="element-22929">
                <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden" data-id="element-22930">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-22931">
                    <h4 className="text-base font-medium text-gray-900" data-id="element-22932">
                      {editingBankAccount ? 'Edit Bank Account' : 'Add New Bank Account'}
                    </h4>
                  </div>
                  <div className="p-4" data-id="element-22933">
                    <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2" data-id="element-22934">
                      <div data-id="element-22935">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22936">
                          Bank Name <span className="text-red-500" data-id="element-22937">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.bankName ? 'border-red-300' : ''}`} value={formData.bankName} name="bankName" onChange={handleInputChange} placeholder="Bank name" data-id="element-22938" />
                        {errors.bankName && <p className="mt-1 text-sm text-red-600" data-id="element-22939">
                            {errors.bankName}
                          </p>}
                      </div>
                      <div data-id="element-22940">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22941">
                          Account Name <span className="text-red-500" data-id="element-22942">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.accountName ? 'border-red-300' : ''}`} value={formData.accountName} name="accountName" onChange={handleInputChange} placeholder="Account holder name" data-id="element-22943" />
                        {errors.accountName && <p className="mt-1 text-sm text-red-600" data-id="element-22944">
                            {errors.accountName}
                          </p>}
                      </div>
                      <div data-id="element-22945">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22946">
                          Account Number <span className="text-red-500" data-id="element-22947">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.accountNumber ? 'border-red-300' : ''}`} value={formData.accountNumber} name="accountNumber" onChange={handleInputChange} placeholder="Account number" data-id="element-22948" />
                        {errors.accountNumber && <p className="mt-1 text-sm text-red-600" data-id="element-22949">
                            {errors.accountNumber}
                          </p>}
                      </div>
                      <div data-id="element-22950">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22951">
                          Routing/BSB/Sort Code
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.routingNumber} name="routingNumber" onChange={handleInputChange} placeholder="Routing/BSB/Sort code" data-id="element-22952" />
                      </div>
                      <div data-id="element-22953">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22954">
                          SWIFT/BIC Code
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.swiftCode} name="swiftCode" onChange={handleInputChange} placeholder="SWIFT/BIC code" data-id="element-22955" />
                      </div>
                      <div data-id="element-22956">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22957">
                          IBAN
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.iban} name="iban" onChange={handleInputChange} placeholder="IBAN (if applicable)" data-id="element-22958" />
                      </div>
                      <div data-id="element-22959">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22960">
                          Account Type
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.accountType} name="accountType" onChange={handleInputChange} data-id="element-22961">
                          {accountTypes.map(type => <option key={type} value={type} data-id="element-22962">
                              {type}
                            </option>)}
                        </select>
                      </div>
                      <div className="flex items-center mt-4" data-id="element-22963">
                        <input id="isPrimary" name="isPrimary" type="checkbox" checked={formData.isPrimary} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-22964" />
                        <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-700" data-id="element-22965">
                          Primary Bank Account
                        </label>
                      </div>
                    </div>
                    <div className="mt-4 flex space-x-3" data-id="element-22966">
                      <button type="button" onClick={handleAddBankAccount} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22967">
                        {editingBankAccount ? 'Update Bank Account' : 'Add Bank Account'}
                      </button>
                      <button type="button" onClick={cancelBankAccountEdit} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22968">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>}
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-22969">
              {bankAccounts.length > 0 ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-22970">
                  {bankAccounts.map(account => <div key={account.id} className={`relative rounded-lg border ${account.isPrimary ? 'border-indigo-500' : 'border-gray-300'} bg-white px-4 py-3 shadow-sm flex flex-col space-y-2`} data-id="element-22971">
                      {account.isPrimary && <span className="absolute top-0 right-0 -mt-2 -mr-2 px-2 py-1 text-xs font-bold rounded-full bg-indigo-100 text-indigo-800 flex items-center" data-id="element-22972">
                          <CheckIcon className="h-3 w-3 mr-1" data-id="element-22973" />
                          Primary
                        </span>}
                      <div className="flex items-center space-x-3" data-id="element-22974">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-22975">
                          <div className="h-5 w-5 text-gray-500" data-id="element-22976" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-22977">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-22978">
                            {account.bankName}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-22979">
                        <p data-id="element-22980">Account Name: {account.accountName}</p>
                        <p data-id="element-22981">
                          Account Number: •••• {account.accountNumber.slice(-4)}
                        </p>
                        <p data-id="element-22982">Account Type: {account.accountType}</p>
                        {account.routingNumber && <p data-id="element-22983">Routing: {account.routingNumber}</p>}
                      </div>
                      <div className="flex justify-end space-x-2 mt-2" data-id="element-22984">
                        <button type="button" onClick={() => handleEditBankAccount(account)} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-22985">
                          Edit
                        </button>
                        <button type="button" onClick={() => removeBankAccount(account.id)} className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-22986">
                          Remove
                        </button>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-4 text-sm text-gray-500" data-id="element-22987">
                  No bank accounts added yet. Add at least one bank account for
                  payment processing.
                </div>}
            </div>
          </div>
          {/* Organisation Unit Availability */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-22988">
            <div className="px-4 py-5 sm:px-6" data-id="element-22989">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22990">
                Organisation Unit Availability
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-22991">
                Define which organisation units can use this vendor
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-22992">
              <div className="flex items-center mb-4" data-id="element-22993">
                <input id="isGlobal" name="isGlobal" type="checkbox" checked={formData.isGlobal} onChange={e => handleAvailabilityChange(e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-22994" />
                <label htmlFor="isGlobal" className="ml-2 block text-sm text-gray-700" data-id="element-22995">
                  Global Vendor (available to all entities)
                </label>
              </div>
              {!formData.isGlobal && <div className="mt-4 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-22996">
                  <div className="sm:col-span-4" data-id="element-22997">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22998">
                      Organisation Unit <span className="text-red-500" data-id="element-22999">*</span>
                    </label>
                    <select className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.orgUnitId ? 'border-red-300' : ''}`} name="orgUnitId" value={formData.orgUnitId || ''} onChange={handleInputChange} data-id="element-23000">
                      <option value="" data-id="element-23001">Select an organisation unit</option>
                      {orgUnits.map(orgUnit => <option key={orgUnit.id} value={orgUnit.id} data-id="element-23002">
                          {orgUnit.path} - {orgUnit.name}
                        </option>)}
                    </select>
                    {errors.orgUnitId && <p className="mt-1 text-sm text-red-600" data-id="element-23003">
                        {errors.orgUnitId}
                      </p>}
                  </div>
                  <div className="sm:col-span-2" data-id="element-23004">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-23005">
                      Include Children
                    </label>
                    <div className="flex items-center h-10" data-id="element-23006">
                      <input type="checkbox" name="includeChildOrgUnits" id="includeChildOrgUnits" checked={formData.includeChildOrgUnits || false} onChange={handleInputChange} disabled={!formData.orgUnitId} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50" data-id="element-23007" />
                      <label htmlFor="includeChildOrgUnits" className="ml-2 text-sm text-gray-700" data-id="element-23008">
                        Include child org units
                      </label>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
          {/* Documents */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-23009">
            <div className="px-4 py-5 sm:px-6" data-id="element-23010">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-23011">
                Documents
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-23012">
                Upload supporting documents
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-23013">
              <FileUpload onFileUpload={handleFileUpload} data-id="element-23014" />
              {files.length > 0 && <div className="mt-4" data-id="element-23015">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-23016">
                    Files to Upload
                  </h4>
                  <ul className="border rounded-md divide-y divide-gray-200" data-id="element-23017">
                    {files.map((file, index) => <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" data-id="element-23018">
                        <div className="w-0 flex-1 flex items-center" data-id="element-23019">
                          <svg className="flex-shrink-0 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-23020">
                            <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd" data-id="element-23021" />
                          </svg>
                          <span className="ml-2 flex-1 w-0 truncate" data-id="element-23022">
                            {file.name}
                          </span>
                        </div>
                        <div className="ml-4 flex-shrink-0" data-id="element-23023">
                          <button type="button" onClick={() => removeFile(index)} className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-23024">
                            Remove
                          </button>
                        </div>
                      </li>)}
                  </ul>
                </div>}
            </div>
          </div>
          {/* Submit Button */}
          <div className="flex justify-end" data-id="element-23025">
            <button type="button" onClick={() => setCurrentView('vendors')} className="mr-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-23026">
              Cancel
            </button>
            <button type="button" onClick={handleSubmit} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-23027">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-23028" />
              Save Vendor
            </button>
          </div>
        </> :
    // Send Invitation Form
    <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-23029">
          <div className="px-4 py-5 sm:px-6" data-id="element-23030">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-23031">
              Send Vendor Invitation
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-23032">
              Invite a vendor to complete their own onboarding
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5" data-id="element-23033">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-23034">
              <div className="sm:col-span-3" data-id="element-23035">
                <label htmlFor="inviteName" className="block text-sm font-medium text-gray-700" data-id="element-23036">
                  Vendor Name <span className="text-red-500" data-id="element-23037">*</span>
                </label>
                <div className="mt-1" data-id="element-23038">
                  <input type="text" name="name" id="inviteName" value={inviteData.name} onChange={handleInviteInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.inviteName ? 'border-red-300' : ''}`} data-id="element-23039" />
                  {errors.inviteName && <p className="mt-1 text-sm text-red-600" data-id="element-23040">
                      {errors.inviteName}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-23041">
                <label htmlFor="inviteType" className="block text-sm font-medium text-gray-700" data-id="element-23042">
                  Vendor Type
                </label>
                <div className="mt-1" data-id="element-23043">
                  <select id="inviteType" name="type" value={inviteData.type} onChange={handleInviteInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-23044">
                    <option value="" data-id="element-23045">Select vendor type</option>
                    {vendorTypes.map(type => <option key={type} value={type} data-id="element-23046">
                        {type}
                      </option>)}
                  </select>
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-23047">
                <label htmlFor="inviteContactName" className="block text-sm font-medium text-gray-700" data-id="element-23048">
                  Contact Name <span className="text-red-500" data-id="element-23049">*</span>
                </label>
                <div className="mt-1" data-id="element-23050">
                  <input type="text" name="contactName" id="inviteContactName" value={inviteData.contactName} onChange={handleInviteInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.inviteContactName ? 'border-red-300' : ''}`} data-id="element-23051" />
                  {errors.inviteContactName && <p className="mt-1 text-sm text-red-600" data-id="element-23052">
                      {errors.inviteContactName}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-23053">
                <label htmlFor="inviteEmail" className="block text-sm font-medium text-gray-700" data-id="element-23054">
                  Email <span className="text-red-500" data-id="element-23055">*</span>
                </label>
                <div className="mt-1" data-id="element-23056">
                  <input type="email" name="email" id="inviteEmail" value={inviteData.email} onChange={handleInviteInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.inviteEmail ? 'border-red-300' : ''}`} data-id="element-23057" />
                  {errors.inviteEmail && <p className="mt-1 text-sm text-red-600" data-id="element-23058">
                      {errors.inviteEmail}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-23059">
                <label htmlFor="invitePhone" className="block text-sm font-medium text-gray-700" data-id="element-23060">
                  Phone
                </label>
                <div className="mt-1" data-id="element-23061">
                  <input type="tel" name="phone" id="invitePhone" value={inviteData.phone} onChange={handleInviteInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-23062" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-23063">
                <label htmlFor="inviteRole" className="block text-sm font-medium text-gray-700" data-id="element-23064">
                  Role
                </label>
                <div className="mt-1" data-id="element-23065">
                  <input type="text" name="role" id="inviteRole" value={inviteData.role} onChange={handleInviteInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-23066" />
                </div>
              </div>
              <div className="sm:col-span-6" data-id="element-23067">
                <label htmlFor="inviteMessage" className="block text-sm font-medium text-gray-700" data-id="element-23068">
                  Invitation Message
                </label>
                <div className="mt-1" data-id="element-23069">
                  <textarea id="inviteMessage" name="message" rows={4} value={inviteData.message} onChange={handleInviteInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-23070" />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end" data-id="element-23071">
              <button type="button" onClick={() => setCurrentView('vendors')} className="mr-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-23072">
                Cancel
              </button>
              <button type="button" onClick={handleSubmit} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-23073">
                <UserPlusIcon className="h-4 w-4 mr-2" data-id="element-23074" />
                Send Invitation
              </button>
            </div>
          </div>
        </div>}
    </div>;
}